import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_FEATURE_CODE_LIST, FEATURE_CODE_CATALOGUE, featureCodeInfo, featureCodeProblem,
  featureCodeRowProblems,
  type FeatureCodeAction, type FeatureCodeListRow, type FeatureCodeRow, type FeatureCodeTargetKind,
  type FeatureCodes,
} from '@arcnet/contracts';
import { api, ApiClientError } from '../../lib/api/client.js';
import { useExtensions } from '../../lib/api/hooks.js';
import {
  tmsKeys, useConferenceRooms, useFeatureCodes, useIvrs, useParkingLots, useQueues, useRingGroups,
} from '../../lib/api/hooks-tms.js';
import { ErrorBanner, TableSkeleton } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';

/**
 * Feature codes: what this account's phones dial to do something themselves (docs/31).
 *
 * A list of code, action and target. Several codes can take one action, a code can name the
 * extension it acts on or the place it calls, and a code can be switched off without losing
 * it. The rules that stop two codes colliding are the API's own (packages/contracts), checked
 * here as the list is edited.
 */

/** A row being edited, with a key React can hold while its code changes. */
interface Draft extends FeatureCodeRow { key: string }

let nextKey = 0;
const keyed = (r: FeatureCodeRow): Draft => ({ ...r, key: `fc${nextKey++}` });

const saved = ({ code, action, target_kind, target_id, target_number, label, enabled }: FeatureCodeRow): FeatureCodeRow =>
  ({ code, action, target_kind, target_id, target_number, label, enabled });

const clean = (d: Draft): FeatureCodeRow => saved({
  ...d,
  code: d.code.trim(),
  target_number: d.target_kind === 'number' ? d.target_number?.trim() || null : null,
  label: d.label?.trim() || null,
});

/** A new row's code: the action's usual one when it is free, else the first free from *50. */
function freeCode(action: FeatureCodeAction, taken: ReadonlySet<string>): string {
  const suggested = featureCodeInfo(action).suggested;
  if (suggested && !taken.has(suggested)) return suggested;
  for (let n = 50; n < 1000; n++) if (!taken.has(`*${n}`)) return `*${n}`;
  return '';
}

/** A target that suits the action, as the row starts or when its action changes. */
function targetFor(action: FeatureCodeAction): Pick<FeatureCodeRow, 'target_kind' | 'target_id' | 'target_number'> {
  const rule = featureCodeInfo(action).target;
  if (rule === 'number') return { target_kind: 'number', target_id: null, target_number: '' };
  return { target_kind: null, target_id: null, target_number: null };
}

const GROUPS = [...new Set(FEATURE_CODE_CATALOGUE.map((c) => c.group))];

/** A list as a set, for "has it changed": the order rows are shown in is not a change. */
const canon = (rows: readonly FeatureCodeRow[]): string =>
  JSON.stringify([...rows].sort((a, b) => a.code.localeCompare(b.code) || a.action.localeCompare(b.action)));

const KIND_GROUP: ReadonlyArray<readonly [FeatureCodeTargetKind, string]> = [
  ['extension', 'Extensions'], ['ring_group', 'Hunt lists'], ['queue', 'Queues'],
  ['ivr', 'IVR menus'], ['conference', 'Conference rooms'], ['parking', 'Parking lots'],
];

export function FeatureCodesTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const writable = can('featurecode:write');
  const current = useFeatureCodes(tenantId);
  const [draft, setDraft] = useState<Draft[] | null>(null);

  // Seeded from what is saved, and again after every save, so the rows never show a code the
  // switch is not actually using.
  useEffect(() => {
    if (current.data) setDraft(current.data.list.map(keyed));
  }, [current.data]);

  const save = useMutation({
    mutationFn: (codes: FeatureCodeRow[]) =>
      api.put<FeatureCodes>(`/v1/tenants/${tenantId}/feature-codes`, { codes }),
    onSuccess: (data) => qc.setQueryData(tmsKeys.featureCodes(tenantId), data),
  });

  const rows = useMemo(() => (draft ?? []).map(clean), [draft]);
  const problems = featureCodeRowProblems(rows);
  const serverErrors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};
  const savedRows = current.data?.list.map(saved) ?? [];
  const changed = draft !== null && canon(rows) !== canon(savedRows);
  const blocked = problems.some((p) => Object.keys(p).length > 0);
  const byKey = new Map((current.data?.list ?? []).map((r) => [`${r.code}|${r.action}|${r.target_id}`, r]));
  const onDefaults = canon(rows) === canon(DEFAULT_FEATURE_CODE_LIST);

  const update = (key: string, patch: Partial<FeatureCodeRow>) =>
    setDraft((d) => (d ?? []).map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) => setDraft((d) => (d ?? []).filter((r) => r.key !== key));
  const add = () => setDraft((d) => {
    const list = d ?? [];
    const taken = new Set(list.map((r) => r.code.trim()));
    const action: FeatureCodeAction = 'call_target';
    return [...list, keyed({
      code: freeCode(action, taken), action, ...targetFor(action), label: null, enabled: true,
    })];
  });
  const setAction = (r: Draft, action: FeatureCodeAction) => {
    const taken = new Set((draft ?? []).filter((x) => x.key !== r.key).map((x) => x.code.trim()));
    // A code the person has not changed follows the action; one they typed stays.
    const typed = r.code.trim() !== (featureCodeInfo(r.action).suggested ?? '') && r.code.trim() !== '';
    update(r.key, {
      action, ...targetFor(action),
      ...(typed && !taken.has(r.code.trim()) ? {} : { code: freeCode(action, taken) }),
    });
  };

  if (current.error) return <ErrorBanner error={current.error} />;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Feature codes</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What this account's phones dial to do something themselves.
          </p>
        </div>
        {writable && (
          <div className="arc-row" style={{ gap: 8 }}>
            <button type="button" className="arc-btn" disabled={save.isPending || onDefaults}
              onClick={() => setDraft(DEFAULT_FEATURE_CODE_LIST.map(keyed))}>
              Use the defaults
            </button>
            <button type="button" className="arc-btn" disabled={save.isPending || draft === null} onClick={add}>
              <Icon name="plus" /> Add code
            </button>
            <button type="button" className="arc-btn arc-btn-primary"
              disabled={save.isPending || !changed || blocked}
              onClick={() => save.mutate(rows)}>
              {save.isPending ? 'Saving…' : 'Save codes'}
            </button>
          </div>
        )}
      </div>

      {save.error && !(save.error instanceof ApiClientError && Object.keys(serverErrors).length > 0) && (
        <ErrorBanner error={save.error} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Code</th>
                <th style={{ width: 250 }}>Does</th>
                <th>On or to</th>
                <th style={{ width: 150 }}>Key label</th>
                <th style={{ width: 56 }}>On</th>
                {writable && <th style={{ width: 40 }} aria-label="Remove" />}
              </tr>
            </thead>
            {draft === null ? <TableSkeleton rows={10} cols={writable ? 6 : 5} /> : (
              <tbody>
                {draft.length === 0 && (
                  <tr><td colSpan={writable ? 6 : 5} className="arc-muted">No codes. The phones dial nothing special.</td></tr>
                )}
                {draft.map((r, i) => {
                  const info = featureCodeInfo(r.action);
                  const codeError = serverErrors[`codes.${i}.code`]
                    ?? (r.code.trim() === '' ? featureCodeProblem('') : problems[i]?.code);
                  const targetError = serverErrors[`codes.${i}.target_kind`] ?? problems[i]?.target;
                  const stored = byKey.get(`${r.code.trim()}|${r.action}|${r.target_id}`);
                  const disabled = !writable || save.isPending;
                  return (
                    <tr key={r.key} style={r.enabled ? undefined : { opacity: 0.6 }}>
                      <td>
                        <input className="arc-input arc-mono" value={r.code} inputMode="tel"
                          aria-label={`Code for ${info.group}, ${info.label}`} aria-invalid={codeError !== undefined}
                          disabled={disabled} onChange={(e) => update(r.key, { code: e.target.value })} />
                        {codeError && <div className="arc-error" style={{ fontSize: 11.5, marginTop: 3 }}>{codeError}</div>}
                      </td>
                      <td>
                        <select className="arc-select" value={r.action} disabled={disabled}
                          aria-label={`What ${r.code || 'this code'} does`}
                          onChange={(e) => setAction(r, e.target.value as FeatureCodeAction)}>
                          {GROUPS.map((g) => (
                            <optgroup key={g} label={g}>
                              {FEATURE_CODE_CATALOGUE.filter((c) => c.group === g).map((c) => (
                                <option key={c.action} value={c.action}>{`${c.group}: ${c.label}`}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {info.then && (
                          <div className="arc-subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                            Dial <span className="arc-mono">{r.code.trim()}</span> then {info.then}.
                          </div>
                        )}
                      </td>
                      <td>
                        <TargetCell tenantId={tenantId} row={r} disabled={disabled} stored={stored}
                          onChange={(patch) => update(r.key, patch)} />
                        {targetError && <div className="arc-error" style={{ fontSize: 11.5, marginTop: 3 }}>{targetError}</div>}
                      </td>
                      <td>
                        <input className="arc-input" value={r.label ?? ''} maxLength={24} disabled={disabled}
                          placeholder={info.label} aria-label={`Key label for ${r.code || 'this code'}`}
                          onChange={(e) => update(r.key, { label: e.target.value })} />
                      </td>
                      <td>
                        <Switch checked={r.enabled} disabled={disabled}
                          label={`${r.code || 'This code'} switched on`}
                          onChange={(on) => update(r.key, { enabled: on })} />
                      </td>
                      {writable && (
                        <td>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-danger-ghost"
                            disabled={save.isPending} aria-label={`Remove ${r.code || 'this code'}`}
                            title="Remove" onClick={() => remove(r.key)}>
                            <Icon name="trash" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
      </div>

      <p className="arc-subtle" style={{ fontSize: 11.5, marginTop: 10 }}>
        A change reaches the phones within about a minute. The test numbers 9196 (echo) and
        9195 (tone) are not feature codes and stay as they are.
      </p>
    </>
  );
}

/** What a row acts on or calls: nothing, the dialling phone or an extension, a place, or a number. */
function TargetCell({ tenantId, row, disabled, stored, onChange }: {
  tenantId: string; row: Draft; disabled: boolean; stored: FeatureCodeListRow | undefined;
  onChange: (patch: Partial<FeatureCodeRow>) => void;
}) {
  const rule = featureCodeInfo(row.action).target;
  // Each list under its own read permission; one the role lacks is simply not offered (permission sweep).
  const can = useSession((s) => s.can);
  const extensions = useExtensions(rule === 'none' || rule === 'number' || !can('extension:read') ? null : tenantId, { limit: 200 });
  const needsPlaces = rule === 'destination';
  const place = (perm: string): string => (needsPlaces && can(perm) ? tenantId : '');
  const groups = useRingGroups(place('extension:read'));
  const queues = useQueues(place('queue:read'));
  const ivrs = useIvrs(place('flow:read'));
  const rooms = useConferenceRooms(place('queue:read'));
  const lots = useParkingLots(place('queue:read'));

  if (rule === 'none') return <span className="arc-subtle">—</span>;

  if (rule === 'number') {
    return (
      <input className="arc-input arc-mono" value={row.target_number ?? ''} inputMode="tel" disabled={disabled}
        placeholder="+27215550100" aria-label={`Number ${row.code || 'this code'} dials`}
        onChange={(e) => onChange({ target_kind: 'number', target_id: null, target_number: e.target.value })} />
    );
  }

  const options: Record<FeatureCodeTargetKind, Array<{ id: string; name: string }>> = {
    extension: (extensions.data?.data ?? []).map((e) => ({ id: e.id, name: `${e.number} · ${e.display_name}` })),
    ring_group: (groups.data?.data ?? []).map((g) => ({ id: g.id, name: g.name })),
    queue: (queues.data?.data ?? []).map((q) => ({ id: q.id, name: q.name })),
    ivr: (ivrs.data?.data ?? []).map((m) => ({ id: m.id, name: m.name })),
    conference: (rooms.data?.data ?? []).map((c) => ({ id: c.id, name: c.name })),
    parking: (lots.data?.data ?? []).map((p) => ({ id: p.id, name: p.name })),
    number: [],
  };
  const value = row.target_kind && row.target_id ? `${row.target_kind}:${row.target_id}` : '';
  // A target since deleted still shows, by the name it had, until the row is changed.
  const missing = stored?.target_missing && value !== ''
    && !options[row.target_kind as FeatureCodeTargetKind]?.some((o) => o.id === row.target_id);
  const kinds = rule === 'seat' ? KIND_GROUP.filter(([k]) => k === 'extension') : KIND_GROUP;

  return (
    <>
      <select className="arc-select" value={value} disabled={disabled}
        aria-label={`What ${row.code || 'this code'} ${rule === 'seat' ? 'acts on' : 'calls'}`}
        onChange={(e) => {
          const [kind, id] = e.target.value.split(':');
          onChange(kind && id
            ? { target_kind: kind as FeatureCodeTargetKind, target_id: id, target_number: null }
            : { target_kind: null, target_id: null, target_number: null });
        }}>
        <option value="">{rule === 'seat' ? 'The phone that dials it' : 'Choose where it calls'}</option>
        {missing && <option value={value}>Deleted</option>}
        {kinds.map(([kind, label]) => options[kind].length > 0 && (
          <optgroup key={kind} label={label}>
            {options[kind].map((o) => <option key={o.id} value={`${kind}:${o.id}`}>{o.name}</option>)}
          </optgroup>
        ))}
      </select>
      {missing && <div className="arc-error" style={{ fontSize: 11.5, marginTop: 3 }}>Its target was deleted, so this code does nothing.</div>}
    </>
  );
}
